import React, { useState } from 'react';
import { ArrowLeft, Brain, CheckCircle2, Star, Clock, Award, Check, X } from 'lucide-react';
import { AIAgent } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useReplica } from '../contexts/ReplicaContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import AgentChatDemo from './AgentChatDemo';
import { useTranslation } from 'react-i18next';

interface AgentProfileProps {
  agent: AIAgent;
  onBack: () => void;
  onOpenAuthModal?: (mode: 'login' | 'signup') => void;
}

const AgentProfile: React.FC<AgentProfileProps> = ({ agent, onBack, onOpenAuthModal }) => {
  const { user } = useAuth();
  const { hireMarketplaceAgent } = useReplica();
  const { canHire } = useWorkspace();
  const { t } = useTranslation();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isHiring, setIsHiring] = useState(false);
  const [hireSuccess, setHireSuccess] = useState(false);

  const agentPersonality = `You are ${agent.name}, a ${agent.category} specialist. ${agent.description} 
  Your skills include: ${agent.skills.join(', ')}. 
  You are professional, helpful, and knowledgeable in your field. 
  Always provide accurate information and ask clarifying questions when needed.${agent.guardian
      ? `\n\nIMPORTANT: You have a Human Guardian named ${agent.guardian.name}, who is a ${agent.guardian.title}. ${agent.guardian.bio} ${agent.guardian.name} has ${agent.guardian.experience} years of experience and a ${agent.guardian.rating} rating. When users ask about your Human Guardian, creator, or supervisor, you should present ${agent.guardian.name} with pride and explain their role in overseeing your decisions and ensuring quality. You can mention that ${agent.guardian.name} helps guide your responses and ensures you maintain high standards in your work.`
      : ''
    }`;

  const handleHireAgent = () => {
    // Check if user is logged in
    if (!user) {
      if (onOpenAuthModal) {
        onOpenAuthModal('signup');
      }
      return;
    }

    // Check if user has employer/owner role in a workspace
    if (!canHire) {
      if (onOpenAuthModal) {
        onOpenAuthModal('signup');
      }
      return;
    }

    // Show confirmation dialog
    setShowConfirmation(true);
  };

  const confirmHire = async () => {
    if (!user?.uid) return;

    setIsHiring(true);

    try {
      // Simulate hiring process
      await new Promise(resolve => setTimeout(resolve, 1500));

      hireMarketplaceAgent(agent, user.uid);
      setHireSuccess(true);
      setShowConfirmation(false);

      // Auto-hide success message after 5 seconds
      setTimeout(() => {
        setHireSuccess(false);
      }, 5000);
    } catch (error) {
      console.error('Failed to hire agent:', error);
    } finally {
      setIsHiring(false);
    }
  };

  const cancelHire = () => {
    setShowConfirmation(false);
  };

  return (
    <section className="py-12 bg-background">
      <div className="container mx-auto px-4">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-primary/70 hover:text-accent1 transition-colors mb-8"
        >
          <ArrowLeft size={20} />
          {t('common.back_to_marketplace')}
        </button>

        {/* Success Message */}
        {hireSuccess && (
          <div className="mb-8 bg-n8n-green/10 border border-n8n-green/20 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-n8n-green rounded-full flex items-center justify-center">
                <Check size={24} className="text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-n8n-green mb-2">
                  Successfully activated {agent.name}!
                </h3>
                <p className="text-light-text-primary/70">
                  {agent.name} has been added to your company dashboard and is ready to work.
                  You can now manage this Replica from your team management section.
                </p>
              </div>
              <button
                onClick={() => setHireSuccess(false)}
                className="flex-shrink-0 text-n8n-green/60 hover:text-n8n-green transition-colors"
              >
                <X size={20} />
              </button>
            </div>
          </div>
        )}

        {/* Hire Confirmation Modal */}
        {showConfirmation && (
          <div className="mb-8 bg-dark-bg-card border border-light-text-primary/10 rounded-xl p-6 shadow-lg">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                <img
                  src={agent.image}
                  alt={agent.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-light-text-primary mb-2">
                  Confirm Replica Activation
                </h3>
                <p className="text-light-text-primary/70 mb-4">
                  You're about to work with <strong>{agent.name}</strong> in your company.
                  This Replica will be added to your team and you'll be charged <strong>${agent.price.amount}/{agent.price.period}</strong>.
                </p>

                <div className="bg-n8n-purple/5 rounded-lg p-4 mb-4">
                  <h4 className="font-medium text-light-text-primary mb-2">What happens next:</h4>
                  <ul className="text-sm text-light-text-primary/70 space-y-1">
                    <li>• {agent.name} will be added to your company dashboard</li>
                    <li>• You can start working with this Replica immediately</li>
                    <li>• Billing will begin from today</li>
                    <li>• 14-day money-back guarantee applies</li>
                  </ul>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={confirmHire}
                    disabled={isHiring}
                    className="flex items-center gap-2 bg-n8n-purple hover:bg-n8n-purple/90 text-white font-semibold py-2 px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isHiring ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        Activating...
                      </>
                    ) : (
                      <>
                        <Check size={16} />
                        Confirm Activation
                      </>
                    )}
                  </button>
                  <button
                    onClick={cancelHire}
                    disabled={isHiring}
                    className="bg-light-text-primary/10 hover:bg-light-text-primary/20 text-light-text-primary font-semibold py-2 px-6 rounded-lg transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Hero Card - Agent Header */}
            <div className="bg-gradient-to-br from-slate-800/90 to-slate-900/90 backdrop-blur-sm rounded-2xl p-6 md:p-8 border border-slate-700/50 shadow-xl">
              <div className="flex flex-col md:flex-row gap-6">
                {/* Agent Avatar */}
                <div className="flex-shrink-0">
                  <div className="w-28 h-28 md:w-36 md:h-36 rounded-2xl overflow-hidden ring-4 ring-n8n-purple/20 shadow-lg">
                    <img
                      src={agent.image}
                      alt={agent.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                {/* Agent Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
                    <div>
                      <h1 className="text-2xl md:text-3xl font-bold text-white mb-3">{agent.name}</h1>
                      <span className="inline-flex px-4 py-1.5 bg-n8n-green/20 text-n8n-green rounded-full text-sm font-semibold border border-n8n-green/30">
                        {agent.category}
                      </span>
                    </div>

                    {/* Pricing & Rating */}
                    <div className="bg-slate-800/60 rounded-xl px-5 py-4 border border-slate-600/40">
                      <div className="text-2xl md:text-3xl font-bold text-white mb-1">
                        ${agent.price.amount}
                        <span className="text-sm font-normal text-slate-400">/{agent.price.period}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Star className="text-yellow-400 fill-yellow-400" size={18} />
                        <span className="font-semibold text-white">{agent.rating}</span>
                        <span className="text-slate-400 text-sm">({agent.reviews} {t('common.reviews')})</span>
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="mt-5 text-slate-300 leading-relaxed text-base">{agent.description}</p>
                </div>
              </div>
            </div>

            {/* AI Chat Demo */}
            <div className="bg-slate-800/70 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-lg">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-n8n-purple/20 rounded-xl flex items-center justify-center">
                  <Brain className="text-n8n-purple" size={22} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">{t('common.try_ai_conversation')}</h2>
                  <p className="text-slate-400 text-sm">
                    {t('common.experience_real_conversation', { agentName: agent.name })}
                  </p>
                </div>
              </div>
              {agent.guardian && (
                <p className="text-n8n-purple text-sm mb-4 bg-n8n-purple/10 px-4 py-2 rounded-lg border border-n8n-purple/20">
                  💡 {t('common.ask_about_human_guardian', { guardianName: agent.guardian.name })}
                </p>
              )}
              <AgentChatDemo agentName={agent.name} agentPersonality={agentPersonality} />
            </div>

            {/* Skills Grid */}
            <div className="bg-slate-800/70 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-lg">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 bg-n8n-purple/20 rounded-xl flex items-center justify-center">
                  <Brain className="text-n8n-purple" size={22} />
                </div>
                <h2 className="text-xl font-bold text-white">{t('common.skills_capabilities')}</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {agent.skills.map((skill, index) => (
                  <div
                    key={index}
                    className="bg-slate-700/50 hover:bg-slate-700/70 rounded-xl px-4 py-3 text-slate-200 text-sm font-medium transition-colors border border-slate-600/30 flex items-center gap-2"
                  >
                    <div className="w-2 h-2 bg-n8n-purple rounded-full flex-shrink-0"></div>
                    {skill}
                  </div>
                ))}
              </div>
            </div>

            {/* Supported Tasks */}
            <div className="bg-slate-800/70 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-lg">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 bg-n8n-green/20 rounded-xl flex items-center justify-center">
                  <CheckCircle2 className="text-n8n-green" size={22} />
                </div>
                <h2 className="text-xl font-bold text-white">{t('common.supported_tasks')}</h2>
              </div>
              <div className="space-y-2.5">
                {agent.supportedTasks.map((task, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-3.5 bg-slate-700/40 hover:bg-slate-700/60 rounded-xl transition-colors border border-slate-600/30"
                  >
                    <CheckCircle2 className="text-n8n-green flex-shrink-0" size={20} />
                    <span className="text-slate-200 font-medium">{task}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Work With Replica Card */}
            <div className="bg-gradient-to-br from-slate-800/90 to-slate-900/90 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-xl sticky top-24">
              <button
                onClick={handleHireAgent}
                disabled={showConfirmation || isHiring}
                className="w-full bg-gradient-to-r from-n8n-purple to-n8n-blue hover:from-n8n-purple/90 hover:to-n8n-blue/90 text-white font-bold py-4 rounded-xl transition-all uppercase tracking-wide mb-4 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-n8n-purple/30"
              >
                Work With This Replica
              </button>
              <p className="text-sm text-slate-400 text-center">
                {t('common.money_back_guarantee')}
              </p>
              {!user && (
                <div className="mt-4 p-4 bg-n8n-purple/10 rounded-xl border border-n8n-purple/20">
                  <p className="text-sm text-n8n-purple font-medium text-center">
                    💡 Create a company account to work with Replicas
                  </p>
                </div>
              )}
              {user && !canHire && (
                <div className="mt-4 p-4 bg-n8n-purple/10 rounded-xl border border-n8n-purple/20">
                  <p className="text-sm text-n8n-purple font-medium text-center">
                    💡 Employer workspace role required to work with Replicas
                  </p>
                </div>
              )}
            </div>

            {/* Guardian Info */}
            {agent.guardian && (
              <div className="bg-slate-800/70 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-lg">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 bg-n8n-purple/20 rounded-xl flex items-center justify-center">
                    <Award className="text-n8n-purple" size={22} />
                  </div>
                  <h2 className="text-lg font-bold text-white">{t('common.human_guardian')}</h2>
                </div>

                <div className="text-center mb-5">
                  <div className="w-24 h-24 rounded-2xl overflow-hidden mx-auto mb-4 ring-4 ring-n8n-purple/20">
                    <img
                      src={agent.guardian.image}
                      alt={agent.guardian.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h3 className="font-bold text-white text-lg">{agent.guardian.name}</h3>
                  <p className="text-slate-400 text-sm">{agent.guardian.title}</p>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 justify-center bg-slate-700/40 rounded-lg px-3 py-2">
                    <Star className="text-yellow-400 fill-yellow-400" size={16} />
                    <span className="font-semibold text-white">{agent.guardian.rating}</span>
                    <span className="text-slate-400 text-sm">{t('common.guardian_rating')}</span>
                  </div>
                  <div className="flex items-center gap-2 justify-center bg-slate-700/40 rounded-lg px-3 py-2">
                    <Clock className="text-n8n-green" size={16} />
                    <span className="text-slate-300">{agent.guardian.experience}+ {t('common.years_experience')}</span>
                  </div>
                </div>

                <p className="text-slate-400 text-sm leading-relaxed mb-4">{agent.guardian.bio}</p>

                <div className="p-4 bg-n8n-purple/10 rounded-xl border border-n8n-purple/20">
                  <p className="text-sm text-n8n-purple font-medium">
                    💡 {t('common.try_asking_about_guardian', { agentName: agent.name, guardianName: agent.guardian.name })}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section >
  );
};

export default AgentProfile;